// oxlint-disable react/no-array-index-key -- index is the identity
import { keyframes, styled } from "goober";
import { nanoid } from "nanoid";
import { forwardRef, useEffect, useRef, useState } from "react";
import type { ConnectorWithOAuth } from "../../../shared/connectors/getTargetValue";
import { compact } from "../../lib/compact";
import { uniqueByType } from "../../lib/connector";
import { describeCron } from "../../lib/describeCron";
import { ROUTE_TYPE_ICONS, routeTypeLabel } from "../../lib/routeTypeDisplay";
import {
  playClick,
  playStepSound,
  type StepSoundParams,
  stepSoundBank,
} from "../../lib/sounds";
import {
  advanceStepCompletion,
  type StepCompletionBaseline,
} from "./stepCompletionSignal";
import { Blob } from "../Blob";
import { ConnectorAvatars } from "../ConnectorAvatars";
import { Icon } from "../Icon";
import { Link } from "../Link";
import { LinkTooltip } from "../LinkTooltip";
import { ReadmeDescriptionTooltip } from "../ReadmeDescriptionTooltip";
import { StepPresence } from "../StepPresence";
import { Text } from "../Text";
import { Tooltip } from "../Tooltip";
import type { StepData } from "../Workflow";

const pickSound = () =>
  stepSoundBank[Math.floor(Math.random() * stepSoundBank.length)]!;

const gridStepPadding = 8;
const gridStepLineHeight = 12;
const counterHeight = 16;

const pendingPulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

const shimmer = keyframes`
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`;

const completionPulse = keyframes`
  0% { transform: scale(1); opacity: 1; }
  45% { transform: scale(1.012); opacity: 0.9; }
  100% { transform: scale(1); opacity: 1; }
`;

const cardEntrance = keyframes`
  from { opacity: 0; transform: scale(0.92); }
  to { opacity: 1; transform: scale(1); }
`;

const Container = styled(Link)`
  text-decoration: none;
  --grid-marker-size: 4px;
  position: relative;
  border-radius: 12px;
  --card-bg: var(--200);
  background: var(--card-bg);
  color: var(--1000);
  height: 48px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 0 0 3px var(--surface-primary);
  overflow: visible;
  &:hover,
  &:focus-within {
    z-index: 2;
    --card-bg: var(--400);
  }
  &::before,
  &::after {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    width: 16px;
    z-index: -1;
    pointer-events: none;
    opacity: 0;
    transition: opacity 120ms ease;
  }
  &::before {
    left: 0;
    border-radius: 12px 0 0 12px;
    background: linear-gradient(
      to right,
      color-mix(in srgb, var(--600) 32%, transparent),
      transparent
    );
  }
  &::after {
    right: 0;
    border-radius: 0 12px 12px 0;
    background: linear-gradient(
      to left,
      color-mix(in srgb, var(--600) 32%, transparent),
      transparent
    );
  }
  &[data-glow="up"]::before {
    opacity: 1;
  }
  &[data-glow="down"]::after {
    opacity: 1;
  }
  &.selected {
    --card-bg: var(--400);
    box-shadow:
      0 0 0 3px var(--surface-primary),
      inset 0 0 0 1px var(--600);
    color: var(--1000);
  }
  &.selected.changed {
    box-shadow: 0 0 0 3px var(--surface-primary);
  }
  .dashed-border {
    position: absolute;
    inset: 0;
    pointer-events: none;
    overflow: visible;
  }
  .dashed-border rect {
    stroke: var(--400);
  }
  &:hover .dashed-border rect,
  &:focus-within .dashed-border rect,
  &.selected .dashed-border rect {
    stroke: var(--600);
  }

  .counter {
    background: var(--400);
    color: var(--1000);
    font-size: 9px;
    font-style: normal;
    font-weight: 500;
    line-height: ${gridStepLineHeight}px;
    letter-spacing: -1%;
    display: inline-flex;
    min-width: 16px;
    width: fit-content;
    border-radius: 10px;
    padding: 2px 4px;
    justify-content: center;
  }

  &:hover .counter,
  &:focus-within .counter {
    background: color-mix(in srgb, var(--600) 32%, transparent);
  }

  &.selected .counter {
    background: var(--600);
  }

  &.executed {
    animation: ${completionPulse} 260ms ease-out;
    transform-origin: center;
  }

  &.entering {
    animation: ${cardEntrance} 240ms ease-out both;
  }
`;

const PresenceContainer = styled("div")`
  position: absolute;
  top: -4px;
  right: -4px;
  z-index: 1;
  pointer-events: none;
  & > * {
    pointer-events: auto;
  }
`;

type Props = {
  step: StepData;
  to: string;
  isSelected: boolean;
  isChanged: boolean;
  pos: { row: number; col: number };
  height: number;
  incomingFromStepIds: string[];
  stepColorMap: Map<string, string>;
  stepNameMap: Map<string, string>;
  onContextMenu: (e: React.MouseEvent, step: StepData) => void;
  isEntering?: boolean;
  entranceDelay?: number;
  isDimmed?: boolean;
  onHighlightHover?: (
    hovered: boolean,
    direction: "up" | "down" | "both"
  ) => void;
  isAiPresent: boolean;
  presentUserIds: Set<string>;
  enableSounds: boolean;
  copyableUrl: string | null;
  copyableEmail: string | null;
};

export const GridStep = forwardRef<HTMLAnchorElement, Props>((props, ref) => {
  const {
    step,
    to,
    isSelected,
    isChanged,
    pos,
    height,
    incomingFromStepIds,
    stepColorMap,
    stepNameMap,
    onContextMenu,
    isEntering,
    entranceDelay,
    isDimmed,
    onHighlightHover,
    isAiPresent,
    presentUserIds,
    enableSounds,
    copyableUrl,
    copyableEmail,
  } = props;

  const color = step.color;
  const latestStatus = step.lastStatus;
  const latestCompletedAt = step.lastCompletedAt;
  const isRun = step.lastKind !== "build";
  const isExecuting = isRun && latestStatus === "pending";
  const [showCompletionPulse, setShowCompletionPulse] = useState(false);
  const [edgeGlow, setEdgeGlow] = useState<"up" | "down" | "both" | null>(null);
  const completionBaselineRef = useRef<StepCompletionBaseline>({
    ready: false,
    completedAt: null,
  });
  const soundRef = useRef<StepSoundParams>(pickSound());
  const cardRef = useRef<HTMLAnchorElement>(null);
  const cardAnchorName = useRef(`--step-card-${nanoid(8)}`).current;
  const setCardRef = (node: HTMLAnchorElement | null) => {
    cardRef.current = node;
    if (typeof ref === "function") ref(node);
    else if (ref) ref.current = node;
  };

  useEffect(() => {
    const result = advanceStepCompletion(
      completionBaselineRef.current,
      enableSounds,
      latestCompletedAt
    );
    completionBaselineRef.current = result.baseline;
    if (result.completed) {
      setShowCompletionPulse(true);
      playStepSound(soundRef.current);
    }
  }, [latestCompletedAt, enableSounds]);

  const applyHover = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    const edge = Math.min(48, r.width * 0.3);
    const x = e.clientX - r.left;
    const dir = x < edge ? "up" : x > r.width - edge ? "down" : "both";
    setEdgeGlow(dir);
    onHighlightHover?.(true, dir);
  };

  return (
    <Container
      ref={setCardRef}
      to={to}
      className={`${isSelected ? "selected" : ""}${isChanged ? " changed" : ""}${showCompletionPulse ? " executed" : ""}${isEntering ? " entering" : ""}`}
      data-glow={edgeGlow ?? undefined}
      onMouseEnter={applyHover}
      onMouseMove={applyHover}
      onMouseLeave={() => {
        setEdgeGlow(null);
        onHighlightHover?.(false, "both");
      }}
      onContextMenu={(e) => {
        e.preventDefault();
        playClick();
        onContextMenu(e, step);
      }}
      onAnimationEnd={() => setShowCompletionPulse(false)}
      style={
        {
          "--200": `var(--${color}-200)`,
          "--400": `var(--${color}-400)`,
          "--600": `var(--${color}-600)`,
          "--1000": `var(--${color}-1000)`,
          anchorName: cardAnchorName,
          gridColumn: pos.col + 1,
          gridRow: pos.row + 1,
          zIndex: isSelected ? 2 : 1,
          minWidth: 100,
          padding: gridStepPadding,
          opacity: isDimmed ? 0.6 : undefined,
          filter: isDimmed ? "blur(3px)" : undefined,
          transition: "opacity 200ms ease, filter 200ms ease",
          animationDelay:
            isEntering && entranceDelay ? `${entranceDelay}ms` : undefined,
        } as React.CSSProperties
      }
    >
      {isChanged && (
        <svg className="dashed-border" width="100%" height="100%">
          <rect
            x="0.5"
            y="0.5"
            width="calc(100% - 1px)"
            height="calc(100% - 1px)"
            rx="11.5"
            fill="none"
            strokeWidth="1"
            strokeDasharray="4 2"
          />
        </svg>
      )}
      <PresenceContainer>
        <StepPresence
          presentUserIds={presentUserIds}
          isAiPresent={isAiPresent}
          avatarSize={12}
        />
      </PresenceContainer>

      <div
        style={{
          display: "flex",
          flex: 1,
          minWidth: 0,
          gap: 6,
          animation: isExecuting
            ? `${pendingPulse} 0.85s ease-in-out infinite`
            : undefined,
        }}
      >
        <Blob colors={[`var(--${color}-600)`]} />

        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          <Text variant="text-small-md">
            <div
              style={{
                display: "-webkit-box",
                WebkitLineClamp: Math.round(
                  (height - gridStepPadding * 2 - counterHeight) /
                    gridStepLineHeight -
                    0.5
                ),
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                ...(isExecuting
                  ? {
                      background: `linear-gradient(90deg, var(--1000) 30%, var(--600) 50%, var(--1000) 70%)`,
                      backgroundSize: "200% 100%",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      animation: `${shimmer} 2.5s linear infinite`,
                    }
                  : undefined),
              }}
            >
              {step.name}
            </div>
          </Text>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              minWidth: 0,
              gap: 6,
            }}
          >
            <div className="counter">
              {step.executionCount.toLocaleString()}
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              {incomingFromStepIds.length > 1 && (
                <Tooltip
                  content={
                    <span style={{ display: "block" }}>
                      <Text
                        variant="text-small"
                        color="rgba(255, 255, 255, 0.5)"
                      >
                        Multiple inputs:
                      </Text>
                      <br />
                      <Text variant="text-small-md">
                        {incomingFromStepIds
                          .map((id) => stepNameMap.get(id) ?? "Unknown")
                          .join(", ")}
                      </Text>
                    </span>
                  }
                >
                  <SourceCircles
                    fromStepIds={incomingFromStepIds}
                    stepColorMap={stepColorMap}
                    selfColor={color}
                  />
                </Tooltip>
              )}

              {step.publicRoute &&
                copyableUrl &&
                incomingFromStepIds.length === 0 && (
                  <LinkTooltip url={copyableUrl}>
                    <span style={{ display: "flex", cursor: "pointer" }}>
                      <Icon
                        name="link-2"
                        size={12}
                        color={`var(--${color}-1000)`}
                      />
                    </span>
                  </LinkTooltip>
                )}

              {step.emailAddress &&
                copyableEmail &&
                incomingFromStepIds.length === 0 && (
                  <LinkTooltip
                    url={copyableEmail}
                    openUrl={`mailto:${copyableEmail}`}
                  >
                    <span style={{ display: "flex", cursor: "pointer" }}>
                      <Icon
                        name="at-sign"
                        size={12}
                        color={`var(--${color}-1000)`}
                      />
                    </span>
                  </LinkTooltip>
                )}

              {step.schedule?.cronExpression && (
                <Tooltip content={describeCron(step.schedule.cronExpression)}>
                  <span style={{ display: "flex" }}>
                    <Icon
                      name="clock"
                      size={12}
                      color={`var(--${color}-1000)`}
                    />
                  </span>
                </Tooltip>
              )}

              {step.hasConflicts && (
                <Tooltip content="This step has unresolved merge conflicts">
                  <span style={{ display: "flex" }}>
                    <Icon
                      name="merge"
                      size={12}
                      color={`var(--${color}-1000)`}
                    />
                  </span>
                </Tooltip>
              )}

              {(step.hasOutput || step.routeType) && (
                <Tooltip
                  content={
                    step.routeType
                      ? routeTypeLabel(step.routeType)
                      : "Responds to web request"
                  }
                >
                  <span style={{ display: "flex" }}>
                    <Icon
                      name={
                        step.routeType
                          ? ROUTE_TYPE_ICONS[step.routeType]
                          : "earth"
                      }
                      size={12}
                      color={`var(--${color}-1000)`}
                    />
                  </span>
                </Tooltip>
              )}

              {step.hasStorage && (
                <Tooltip content="Storage">
                  <span style={{ display: "flex" }}>
                    <Icon name="box" size={12} color={`var(--${color}-1000)`} />
                  </span>
                </Tooltip>
              )}

              {isChanged && (
                <Tooltip content="Changed on branch">
                  <span style={{ display: "flex" }}>
                    <Icon
                      name="traffic-cone"
                      size={12}
                      color={`var(--${color}-1000)`}
                    />
                  </span>
                </Tooltip>
              )}

              {step.readmeDescription && (
                <ReadmeDescriptionTooltip
                  description={step.readmeDescription}
                  iconColor={`var(--${color}-1000)`}
                  anchorName={cardAnchorName}
                  anchorRef={cardRef}
                />
              )}
            </div>

            <ConnectorAvatars
              flat
              collapseToFit
              align="end"
              connected={uniqueByType(
                compact(
                  (step.connectedApps ?? []).map((c) => c.connectedApp)
                ) as ConnectorWithOAuth[]
              ).map((app) => ({ id: app.id, name: app.name, app }))}
              disconnected={[
                ...new Set(
                  (step.connectedApps ?? [])
                    .filter((c) => !c.connectedApp)
                    .map((c) => c.appType)
                ),
              ].map((type) => ({ id: `missing-${type}`, appType: type }))}
            />
          </div>
        </div>
      </div>
    </Container>
  );
});

const CircleStack = styled("span")`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 16px;
  padding: 0 3px;
  margin: 0 -2px;
  border-radius: 9999px;
  &:hover {
    --card-bg: color-mix(in srgb, var(--600) 32%, var(--400));
    background: var(--card-bg);
  }
`;

function SourceCircles({
  fromStepIds,
  stepColorMap,
  selfColor,
}: {
  fromStepIds: string[];
  stepColorMap: Map<string, string>;
  selfColor: string;
}) {
  const colors = fromStepIds.map((id) => stepColorMap.get(id) ?? "gray");
  colors.push(selfColor);
  return (
    <CircleStack>
      {colors.map((c, i) => (
        <span
          key={`${i}-${c}`}
          style={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            backgroundColor: `var(--${c}-600)`,
            marginLeft: i > 0 ? -4 : 0,
            boxShadow: i > 0 ? "0 0 0 1px var(--card-bg)" : undefined,
          }}
        />
      ))}
    </CircleStack>
  );
}
